// src/app/cajero/panel/page.tsx
'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';

type AccionCaja = 'APERTURA' | 'ARQUEO' | 'TRANSFERENCIA' | 'GASTOS' | 'RETIROS' | 'CREDITOS' | 'CIERRE' | null;

interface Denominaciones {
  b100: number | ''; b50: number | ''; b20: number | ''; b10: number | ''; b5: number | ''; b1: number | '';
  m50: number | ''; m25: number | ''; m10: number | ''; m05: number | ''; m01: number | '';
}

interface DatosPlataforma { tipo: string; saldo?: string; ventas?: string; premios?: string; sustento_url?: string; nombre?: string; }
interface EstadoDigitales { [platformId: string]: DatosPlataforma; }
interface TransaccionBase { 
  id: string; 
  transaction_type: string; 
  description: string; 
  amount: number; 
  evidence_url: string | null; 
  created_at: string; 
  payment_method: string; 
  client_id?: string; 
  creador?: string; 
}
interface Cliente { id: string; nombres: string; cedula: string; total_debt: number; }

const CATEGORIAS_GASTO = [
  { id: 'UTILITIES', label: '💡 Servicios Básicos' },
  { id: 'SUPPLIES_CLEANING', label: '🧹 Suministros' },
  { id: 'MAINTENANCE', label: '🔧 Mantenimiento' },
  { id: 'MARKETING_PROMO', label: '🎁 Fidelización' },
  { id: 'ADMINISTRATIVE_RENT', label: '🏢 Administrativos' },
  { id: 'TRANSPORTATION', label: '🚕 Transporte' },
  { id: 'PAYROLL', label: '👥 Nómina' },
  { id: 'OTHER', label: '📦 Otros Gastos' }
];

function GestionCajaPageContent() {
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);

  // ==========================================
  // 🛠️ VARIABLES DINÁMICAS DE MODO DEBUG
  // ==========================================
  const [enableTimeMachine, setEnableTimeMachine] = useState(false);
  const [enableDebugReopen, setEnableDebugReopen] = useState(false);

  const [userName, setUserName] = useState('Cajero');
  const [userRole, setUserRole] = useState('CAJERO');
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState('C');
  const [config, setConfig] = useState<any>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [adminAgenciesList, setAdminAgenciesList] = useState<{id: string, name: string, code: string}[]>([]);
  const [currentAgencyId, setCurrentAgencyId] = useState<string | null>(null);

  const [accionActiva, setAccionActiva] = useState<AccionCaja>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [plataformasActivas, setPlataformasActivas] = useState<{id: string, name: string, platform_type: string}[]>([]);
  const [clientesAgencia, setClientesAgencia] = useState<Cliente[]>([]);
  const [codigoAgencia, setCodigoAgencia] = useState('');
  const [nombreAgencia, setNombreAgencia] = useState('');

  const [estadoCaja, setEstadoCaja] = useState<'OPEN' | 'CLOSED' | 'LOADING'>('LOADING');
  const [fechaApertura, setFechaApertura] = useState<string | null>(null);

  const [valores, setValores] = useState<Denominaciones>({ b100: '', b50: '', b20: '', b10: '', b5: '', b1: '', m50: '', m25: '', m10: '', m05: '', m01: '' });
  const [mostrarRestringidos, setMostrarRestringidos] = useState(false); 
  const [datosDigitales, setDatosDigitales] = useState<EstadoDigitales>({});
  const [archivosPlataformas, setArchivosPlataformas] = useState<Record<string, File>>({});
  const [notaProtesta, setNotaProtesta] = useState('');

  const [transData, setTransData] = useState({ tipo: 'INGRESO', subTipo: 'SALE_RECHARGE', monto: '', descripcion: '', plataformaId: '' });
  const [transFile, setTransFile] = useState<File | null>(null); 
  const [listaTransferencias, setListaTransferencias] = useState<TransaccionBase[]>([]);
  const [confirmTransfModal, setConfirmTransfModal] = useState(false);

  const [retiroData, setRetiroData] = useState({ destino: 'BANCO', monto: '', descripcion: '', metodoPago: 'BANK_TRANSFER', plataformaId: '' });
  const [retiroFile, setRetiroFile] = useState<File | null>(null);
  const [confirmRetiroModal, setConfirmRetiroModal] = useState(false);

  const [gastoData, setGastoData] = useState({ tipoOperacion: 'OPERATING_EXPENSE', categoria: 'UTILITIES', monto: '', descripcion: '', metodoPago: 'CASH' });
  const [gastoFile, setGastoFile] = useState<File | null>(null);
  const [listaGastos, setListaGastos] = useState<TransaccionBase[]>([]);

  const [creditoData, setCreditoData] = useState({ tipo: 'OTORGAR', clientId: '', monto: '', descripcion: '' });
  const [creditoFile, setCreditoFile] = useState<File | null>(null);
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientData, setNewClientData] = useState({ cedula: '', nombres: '', telefono: '' });
  const [confirmCreditoModal, setConfirmCreditoModal] = useState(false); 

  const [modalUpload, setModalUpload] = useState<{ isOpen: boolean, type: 'PLATAFORMA' | 'TRANSFERENCIA' | 'GASTO' | 'CREDITO', targetId: string, title: string }>({ isOpen: false, type: 'PLATAFORMA', targetId: '', title: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const [debugDate, setDebugDate] = useState('');

  const [isRefreshingPerms, setIsRefreshingPerms] = useState(false);

  // 💡 FUNCIÓN: Sincronizar permisos en vivo
  const handleSincronizarPermisos = async () => {
    setIsRefreshingPerms(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/auth/refresh', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('access_token', data.access_token);
        
        const base64Url = data.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
        
        setEnableTimeMachine(payload.can_use_time_machine === true);
        setEnableDebugReopen(payload.can_reopen_shift === true);
      }
    } catch (e) {
      console.error("Error sincronizando permisos");
    } finally {
      setIsRefreshingPerms(false);
    }
  };

  useEffect(() => {
    setIsClient(true);
    const token = localStorage.getItem('access_token');
    if (!token) {
      router.push('/login');
      return;
    }
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
      
      const role = (payload.role || 'CAJERO').toUpperCase();
      
      if (role === 'SUPERVISOR') {
        alert("🔒 Acceso Denegado. Los supervisores no pueden operar el Punto de Venta.");
        router.replace('/admin/dashboard');
        return;
      }

      const displayName = payload.full_name || payload.username || 'Cajero';
      setUserName(displayName);
      setUserRole(role);
      
      // 💡 ASIGNAR PERMISOS ESPECIALES DESDE EL TOKEN
      setEnableTimeMachine(payload.can_use_time_machine === true);
      setEnableDebugReopen(payload.can_reopen_shift === true);
      
      if (payload.avatar_url) {
        setUserAvatar(payload.avatar_url.startsWith('http') ? payload.avatar_url : `/api${payload.avatar_url}`);
      } else {
        setUserInitials(displayName.charAt(0).toUpperCase());
      }

      if (role === 'ADMIN' || role === 'ADMINISTRADOR') {
        setIsGlobalAdmin(true);
        fetch('/api/agencies', { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setAdminAgenciesList(data);
              if (data.length > 0) {
                setCurrentAgencyId(data[0].id);
                setCodigoAgencia(data[0].code);
                setNombreAgencia(data[0].name);
              }
            }
          }).catch(() => console.error("Error al cargar agencias para admin"));
      } else {
        setIsGlobalAdmin(false);
        setCurrentAgencyId(payload.agency_id);
      }

    } catch (e) {
      router.push('/login');
    }

    fetch('/api/configuracion', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => { if (data && !data.error) setConfig(data); })
      .catch(() => {});

  }, [router]);

  useEffect(() => {
    if (!currentAgencyId || !isClient) return;
    
    setEstadoCaja('LOADING');
    setAccionActiva(null); 
    setListaTransferencias([]);
    setListaGastos([]);
    
    verificarEstadoCaja();
    fetchPlataformasHabilitadas();
    cargarClientes();
  }, [currentAgencyId, isClient]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    router.push('/login');
  };

  const getFechaProceso = () => {
    if (enableTimeMachine && debugDate) return new Date(debugDate).toISOString();
    return new Date().toISOString();
  };

  const formatearFecha = (fechaISO: string) => {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (accionActiva === 'TRANSFERENCIA' || accionActiva === 'RETIROS') cargarTransferencias();
    if (accionActiva === 'GASTOS') cargarGastos();
    if (accionActiva === 'CREDITOS') {
      cargarTransferencias(); 
      cargarClientes();
    }
  }, [accionActiva]);

  const verificarEstadoCaja = async () => {
    if (!currentAgencyId) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`/api/cajas/agencia/${currentAgencyId}/activa`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data && data.status === 'OPEN') {
          setEstadoCaja('OPEN');
          setFechaApertura(data.opened_at);
        } else {
          setEstadoCaja('CLOSED');
          setFechaApertura(null);
        }
      } else { setEstadoCaja('CLOSED'); }
    } catch (error) { setEstadoCaja('CLOSED'); }
  };

  const fetchPlataformasHabilitadas = async () => {
    if (!currentAgencyId) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`/api/plataformas/agencia/${currentAgencyId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setPlataformasActivas(await res.json());
      
      if (!isGlobalAdmin) {
        try {
          let resAgencia = await fetch(`/api/agencies/${currentAgencyId}`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (!resAgencia.ok) resAgencia = await fetch(`/api/agencias/${currentAgencyId}`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (resAgencia.ok) { 
            const agData = await resAgencia.json(); 
            if (agData.code) setCodigoAgencia(agData.code); 
            if (agData.name) setNombreAgencia(agData.name); 
          }
        } catch (err) {}
      }
    } catch (error) { console.error(error); }
  };

  const cargarClientes = async () => {
    if (!currentAgencyId) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`/api/clientes/agencia/${currentAgencyId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setClientesAgencia(await res.json());
    } catch (error) { console.error(error); }
  };

  const handleReabrirCierre = async () => {
    if (!currentAgencyId) return;
    if (!confirm("⚠️ ¿Estás seguro de que quieres reabrir y modificar tu último cierre? Esto anulará el reporte anterior.")) return;
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch('/api/cajas/debug-reabrir', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ agency_id: currentAgencyId })
      });
      const data = await res.json();
      
      if (res.ok) {
        setEstadoCaja('OPEN');
        setFechaApertura(data.data.opened_at);
        
        if (data.data.bills_detail) {
          setValores({ ...valores, ...data.data.bills_detail });
        }
        
        if (data.data.platforms_detail) {
           const uiData = { ...data.data.platforms_detail };
           delete uiData.apertura_snapshot;
           setDatosDigitales(uiData);
        }

        let notaAnterior = data.data.opening_notes || '';
        notaAnterior = notaAnterior.split(' | Cierre:')[0];
        setNotaProtesta(notaAnterior);

        alert('El turno ha sido reabierto y se cargaron los datos. Modifícalos y vuelve a Cerrar Caja.');
        setAccionActiva('CIERRE');
      } else {
        alert(data.error);
      }
    } catch (e) {
      alert('Fallo de conexión al reabrir caja.');
    } finally {
      setIsLoading(false);
    }
  };

  const cargarTransferencias = async () => {
    if (!currentAgencyId) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`/api/transacciones/agencia/${currentAgencyId}/transferencias`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setListaTransferencias(await res.json());
    } catch (error) { console.error(error); }
  };

  const cargarGastos = async () => {
    if (!currentAgencyId) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`/api/transacciones/agencia/${currentAgencyId}/gastos`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setListaGastos(await res.json());
    } catch (error) { console.error(error); }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return alert("Solo imágenes.");
    if (file.size > 2 * 1024 * 1024) return alert("Límite de 2MB excedido.");

    const token = localStorage.getItem('access_token');
    if (modalUpload.type === 'PLATAFORMA') {
      setArchivosPlataformas(prev => ({ ...prev, [modalUpload.targetId]: file }));
      setModalUpload({ isOpen: false, type: 'PLATAFORMA', targetId: '', title: '' });
      return;
    } 

    setIsUploading(true);
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";

    try {
      const transFormData = new FormData();
      transFormData.append('agency_code', finalAgencyCode);
      transFormData.append('evidencia', file);
      
      const res = await fetch(`/api/transacciones/${modalUpload.targetId}/evidencia`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` }, body: transFormData });
      if (res.ok) {
        if (['TRANSFERENCIA', 'CREDITO'].includes(modalUpload.type)) await cargarTransferencias();
        if (modalUpload.type === 'GASTO') await cargarGastos();
        setModalUpload({ isOpen: false, type: 'PLATAFORMA', targetId: '', title: '' });
      } else alert("Error adjuntando comprobante.");
    } catch (error) { alert("Fallo de red."); } finally { setIsUploading(false); }
  };

  useEffect(() => {
    if (!isClient) return; 
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            if (modalUpload.isOpen && !isUploading) handleFileUpload(file);
            else if (accionActiva === 'TRANSFERENCIA') setTransFile(file); 
            else if (accionActiva === 'GASTOS') setGastoFile(file);
            else if (accionActiva === 'RETIROS') setRetiroFile(file);
            else if (accionActiva === 'CREDITOS') setCreditoFile(file);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [modalUpload.isOpen, isUploading, accionActiva, isClient]);

  const eliminarTransaccion = async (id: string, tipo: 'TRANSFERENCIA' | 'GASTO' | 'CREDITO') => {
    if (!confirm('⚠️ ¿Estás seguro de eliminar esta transacción? Esta acción revertirá los saldos y no se puede deshacer.')) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/transacciones/${id}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        if (['TRANSFERENCIA', 'CREDITO'].includes(tipo)) await cargarTransferencias();
        if (tipo === 'GASTO') await cargarGastos();
      } else {
        alert(data.error || 'Error al eliminar');
      }
    } catch(e) {
      alert('Fallo de red al intentar eliminar la transacción.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAbrirConfirmacionTransf = () => {
    if (!currentAgencyId) return alert("Error: Agencia no detectada en la sesión.");
    if (!transData.monto || Number(transData.monto) <= 0) return alert("Ingrese un monto válido a registrar.");
    if (transData.tipo === 'INGRESO' && transData.subTipo === 'PROVIDER_FUNDING' && !transData.plataformaId) return alert("Seleccione el proveedor que envía los fondos.");

    let descripcionFinal = transData.descripcion;
    if (transData.tipo === 'INGRESO' && transData.subTipo === 'PROVIDER_FUNDING' && !descripcionFinal) {
      const nombreProv = plataformasActivas.find(p => p.id === transData.plataformaId)?.name || 'Proveedor';
      descripcionFinal = `Fondeo B2B recibido de ${nombreProv}`;
    } else if (transData.tipo === 'INGRESO' && transData.subTipo === 'BANK_TO_CASH_TRANSFER' && !descripcionFinal) {
      descripcionFinal = `Fondeo de caja (Efectivización de saldo bancario)`;
    } else if (!descripcionFinal) {
      return alert("Ingrese una referencia o descripción.");
    }

    setTransData(prev => ({ ...prev, descripcion: descripcionFinal }));
    setConfirmTransfModal(true);
  };

  const ejecutarTransferencia = async () => {
    setConfirmTransfModal(false);
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";
    let finalTxType = 'PRIZE_PAYMENT';
    if (transData.tipo === 'INGRESO') finalTxType = transData.subTipo; 

    let user_id = '';
    if (token) { const payload = JSON.parse(atob(token.split('.')[1])); user_id = payload.sub || payload.id; }

    const formData = new FormData();
    formData.append('agency_id', currentAgencyId!);
    formData.append('user_id', user_id);
    formData.append('transaction_type', finalTxType);
    formData.append('description', transData.descripcion);
    formData.append('amount', transData.monto);
    formData.append('payment_method', 'BANK_TRANSFER');
    formData.append('agency_code', finalAgencyCode);
    formData.append('created_at', getFechaProceso()); 
    if (transData.tipo === 'INGRESO' && transData.subTipo === 'PROVIDER_FUNDING') formData.append('platform_id', transData.plataformaId);
    if (transFile) formData.append('evidencia', transFile);

    try {
      const res = await fetch('/api/transacciones', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (res.ok) { 
        setTransData({ tipo: 'INGRESO', subTipo: 'SALE_RECHARGE', monto: '', descripcion: '', plataformaId: '' }); 
        setTransFile(null); 
        await cargarTransferencias(); 
      } 
      else { const data = await res.json(); alert(data.error || "Error registrando."); }
    } catch (err) { alert("Fallo de conexión."); } finally { setIsLoading(false); }
  };

  const handleAbrirConfirmacionRetiro = () => {
    if (!currentAgencyId) return alert("Error: Agencia no detectada en la sesión.");
    if (!retiroData.monto || Number(retiroData.monto) <= 0) return alert("Ingrese monto válido a retirar.");
    if (retiroData.destino === 'PROVEEDOR' && !retiroData.plataformaId) return alert("Seleccione el proveedor al que le entrega fondos.");

    let descPorDefecto = retiroData.descripcion;
    if (!descPorDefecto) {
      if (retiroData.destino === 'BANCO') descPorDefecto = 'Depósito Bancario';
      else if (retiroData.destino === 'PROVEEDOR') {
        const nombreProv = plataformasActivas.find(p => p.id === retiroData.plataformaId)?.name || 'Proveedor';
        descPorDefecto = `Pago a recaudador de ${nombreProv}`;
      } else {
        descPorDefecto = 'Entrega a Supervisor';
      }
    }
    setRetiroData(prev => ({ ...prev, descripcion: descPorDefecto }));
    setConfirmRetiroModal(true);
  };

  const ejecutarRetiro = async () => {
    setConfirmRetiroModal(false);
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";
    let user_id = '';
    if (token) { const payload = JSON.parse(atob(token.split('.')[1])); user_id = payload.sub || payload.id; }

    let txType = 'CASH_TO_VAULT_TRANSFER';
    if (retiroData.destino === 'BANCO') txType = 'CASH_TO_BANK_TRANSFER';
    if (retiroData.destino === 'PROVEEDOR') txType = 'PROVIDER_PAYMENT';

    const formData = new FormData();
    formData.append('agency_id', currentAgencyId!);
    formData.append('user_id', user_id);
    formData.append('transaction_type', txType);
    formData.append('description', retiroData.descripcion);
    formData.append('amount', retiroData.monto);
    formData.append('payment_method', retiroData.destino === 'PROVEEDOR' ? retiroData.metodoPago : 'CASH'); 
    formData.append('agency_code', finalAgencyCode);
    formData.append('created_at', getFechaProceso()); 
    if (retiroData.destino === 'PROVEEDOR') formData.append('platform_id', retiroData.plataformaId);
    if (retiroFile) formData.append('evidencia', retiroFile);

    try {
      const res = await fetch('/api/transacciones', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (res.ok) { 
        setRetiroData({ destino: 'BANCO', monto: '', descripcion: '', metodoPago: 'BANK_TRANSFER', plataformaId: '' }); 
        setRetiroFile(null); 
        await cargarTransferencias();
      } else { const data = await res.json(); alert(data.error || "Error registrando la operación."); }
    } catch (err) { alert("Fallo de conexión."); } finally { setIsLoading(false); }
  };

  const handleAbrirConfirmacionCredito = () => {
    if (!currentAgencyId) return alert("Error: Agencia no detectada en la sesión.");
    if (!creditoData.monto || Number(creditoData.monto) <= 0) return alert("Ingrese un monto válido a registrar.");
    
    if (isNewClient) {
      if (!newClientData.cedula || !newClientData.nombres || !newClientData.telefono) {
        return alert("⚠️ Para crear un cliente nuevo debe ingresar Cédula, Nombres y Celular obligatoriamente.");
      }
    } else {
      if (!creditoData.clientId) return alert("Seleccione un cliente válido de la lista.");
    }

    setConfirmCreditoModal(true);
  };

  const ejecutarCreditoAbono = async () => {
    setConfirmCreditoModal(false);
    setIsLoading(true);
    let finalClientId = creditoData.clientId;
    
    const token = localStorage.getItem('access_token');
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";
    
    let user_id = '';
    let cajeroName = userName;
    if (token) { 
      const payload = JSON.parse(atob(token.split('.')[1])); 
      user_id = payload.sub || payload.id; 
      if(payload.full_name || payload.username) cajeroName = payload.full_name || payload.username;
    }

    try {
      if (isNewClient) {
        const resCli = await fetch('/api/clientes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            agency_id: currentAgencyId,
            full_name: newClientData.nombres,
            identification_number: newClientData.cedula,
            phone_number: newClientData.telefono
          })
        });
        const dataCli = await resCli.json();
        if (!resCli.ok) throw new Error(dataCli.error || "Error al registrar el nuevo cliente en la base de datos.");
        finalClientId = dataCli.id; 
      }

      let descripcionFinal = creditoData.descripcion;
      let clienteNombre = isNewClient ? newClientData.nombres : (clientesAgencia.find(c => c.id === finalClientId)?.nombres || 'Cliente');

      if (!descripcionFinal) {
        descripcionFinal = creditoData.tipo === 'ABONO' ? `Cobro deuda a: ${clienteNombre}` : `Aumento deuda a: ${clienteNombre}`;
      }
      
      const finalTxType = creditoData.tipo === 'ABONO' ? 'CLIENT_DEBT_PAYMENT' : 'SALE_RECHARGE';
      const paymentMethod = creditoData.tipo === 'ABONO' ? 'CASH' : 'CREDIT';

      const formData = new FormData();
      formData.append('agency_id', currentAgencyId!);
      formData.append('user_id', user_id);
      formData.append('transaction_type', finalTxType);
      formData.append('description', descripcionFinal);
      formData.append('amount', creditoData.monto);
      formData.append('payment_method', paymentMethod);
      formData.append('agency_code', finalAgencyCode);
      formData.append('client_id', finalClientId);
      formData.append('created_at', getFechaProceso()); 
      if (creditoFile) formData.append('evidencia', creditoFile);

      const res = await fetch('/api/transacciones', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      
      if (res.ok) { 
        fetch('/api/notificaciones/credito', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            agencia: nombreAgencia || finalAgencyCode,
            cajero: cajeroName,
            tipo: creditoData.tipo, 
            monto: creditoData.monto,
            cliente: clienteNombre,
            descripcion: descripcionFinal
          })
        }).catch(e => console.error("Silencio: Error webhook notificación crédito", e));

        setCreditoData({ tipo: 'OTORGAR', clientId: '', monto: '', descripcion: '' }); 
        setCreditoFile(null); 
        setIsNewClient(false);
        setNewClientData({ cedula: '', nombres: '', telefono: '' });
        await cargarTransferencias(); 
        await cargarClientes(); 
      } 
      else { 
        const data = await res.json(); 
        alert(data.error || "Error registrando la operación de crédito."); 
      }
    } catch (err: any) { 
      alert(err.message || "Fallo de conexión al procesar el crédito."); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const registrarGasto = async () => {
    if (!currentAgencyId) return alert("Error: Agencia no detectada en la sesión.");
    if (!gastoData.monto || !gastoData.descripcion) return alert("Ingrese monto y descripción.");
    setIsLoading(true);
    
    const token = localStorage.getItem('access_token');
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";
    let user_id = '';
    
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      user_id = payload.sub || payload.id;
    }

    const formData = new FormData();
    formData.append('agency_id', currentAgencyId);
    formData.append('user_id', user_id);
    formData.append('transaction_type', 'OPERATING_EXPENSE');
    formData.append('amount', gastoData.monto);
    formData.append('description', gastoData.descripcion);
    formData.append('payment_method', gastoData.metodoPago);
    formData.append('category', gastoData.categoria);
    formData.append('agency_code', finalAgencyCode);
    formData.append('created_at', getFechaProceso()); 
    if (gastoFile) formData.append('evidencia', gastoFile);

    try {
      const res = await fetch('/api/transacciones/gasto', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (res.ok) {
        setGastoData({ tipoOperacion: 'OPERATING_EXPENSE', categoria: 'UTILITIES', monto: '', descripcion: '', metodoPago: 'CASH' });
        setGastoFile(null); 
        await cargarGastos(); 
      } else { const data = await res.json(); alert(data.error || "Error registrando el movimiento."); }
    } catch (err) { alert("Fallo de conexión."); } finally { setIsLoading(false); }
  };

  const confirmarYEjecutarAccion = async () => {
    if (!currentAgencyId) return alert("Error: Agencia no detectada en la sesión.");
    setShowSummaryModal(false);
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    let user_id = ''; let cajeroUsername = "Cajero";
    
    if (token) {
      const payloadDecoded = JSON.parse(atob(token.split('.')[1]));
      user_id = payloadDecoded.sub || payloadDecoded.id; 
      cajeroUsername = payloadDecoded.username || 'Cajero';
    }
    
    const finalAgencyCode = codigoAgencia || "Ag_Simulada";

    const finalDatosDigitales: EstadoDigitales = { ...datosDigitales };

    plataformasActivas.forEach(plat => {
      if (!finalDatosDigitales[plat.id]) { finalDatosDigitales[plat.id] = { tipo: plat.platform_type }; }
      finalDatosDigitales[plat.id].nombre = plat.name; 
    });

    const plataformasConImagen = Object.keys(archivosPlataformas);

    if (plataformasConImagen.length > 0) {
      try {
        const uploadPromises = plataformasConImagen.map(async (platId) => {
          const file = archivosPlataformas[platId];
          const platformName = plataformasActivas.find(p => p.id === platId)?.name || 'Plataforma';
          const formData = new FormData();
          formData.append('local', finalAgencyCode);
          formData.append('servicio', platformName);
          formData.append('file', file);
          const res = await fetch('/api/upload', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
          const data = await res.json();
          if (res.ok && data.url) {
            if (!finalDatosDigitales[platId]) finalDatosDigitales[platId] = { tipo: plataformasActivas.find(p => p.id === platId)?.platform_type || '' };
            finalDatosDigitales[platId].sustento_url = data.url;
          } else throw new Error(`Fallo imagen ${platformName}`);
        });
        await Promise.all(uploadPromises); 
      } catch (error) { 
        alert("Problema subiendo las capturas."); 
        setIsLoading(false); 
        return; 
      }
    }

    const payload = { 
      agency_id: currentAgencyId, 
      user_id, 
      efectivo_declarado: totalCalculado, 
      desglose_billetes: valores, 
      datos_plataformas: finalDatosDigitales, 
      nota: notaProtesta,
      created_at: getFechaProceso() 
    };
    
    let endpointUrl = '';
    if (accionActiva === 'APERTURA') endpointUrl = '/api/cajas/abrir';
    else if (accionActiva === 'CIERRE') endpointUrl = '/api/cajas/cerrar';
    else if (accionActiva === 'ARQUEO') endpointUrl = '/api/cajas/micro-arqueo';

    try {
      const res = await fetch(endpointUrl, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
        body: JSON.stringify(payload) 
      });
      const data = await res.json();
      
      if (res.ok) {
        if (accionActiva === 'APERTURA' || accionActiva === 'CIERRE') {
          const endpointNotif = accionActiva === 'APERTURA' ? '/api/notificaciones/apertura' : '/api/notificaciones/cierre';
          
          const payloadNotif = {
            agencia: nombreAgencia || finalAgencyCode,
            cajero: data.cajero_real || cajeroUsername,
            ...(accionActiva === 'APERTURA' && { 
                monto_inicial: totalCalculado,
                observacion: notaProtesta || 'Sin novedades'
            }),
            ...(accionActiva === 'CIERRE' && { 
                monto_declarado: totalCalculado, 
                monto_calculado: data.monto_calculado || 0,
                diferencia: data.diferencia || 0,
                observacion: notaProtesta || 'Sin observaciones',
                
                total_producido: data.total_producido || 0,
                banco_ventas: data.banco_ventas || 0,
                banco_egresos: data.banco_egresos || 0,
                caja_ventas: data.caja_ventas || 0,
                caja_gastos: data.caja_gastos || 0,
                caja_retiros: data.caja_retiros || 0,
                fondeo_caja: data.fondeo_caja || 0,
                reporte_plataformas: data.reporte_plataformas || '',
                fecha_turno: data.fecha_apertura ? new Date(data.fecha_apertura).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES'),
                hora_cierre: data.fecha_cierre ? new Date(data.fecha_cierre).toLocaleString('es-ES') : new Date().toLocaleString('es-ES')
            })
          };

          fetch(endpointNotif, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(payloadNotif)
          }).catch(e => console.error("Silencio: Error disparando webhook de notificación", e));
        }

        alert(data.message || `Operación registrada exitosamente.`);
        setAccionActiva(null);
        setValores({ b100: '', b50: '', b20: '', b10: '', b5: '', b1: '', m50: '', m25: '', m10: '', m05: '', m01: '' });
        setDatosDigitales({}); 
        setArchivosPlataformas({}); 
        setNotaProtesta('');
        await verificarEstadoCaja(); 

      } else {
        alert(data.error);
      }
    } catch (error) { 
      alert("Fallo de conexión con el servidor maestro."); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const totalCalculado = Number((
    (Number(valores.b100) * 100) + (Number(valores.b50) * 50) + (Number(valores.b20) * 20) + (Number(valores.b10) * 10) + (Number(valores.b5) * 5) + (Number(valores.b1) * 1) +
    (Number(valores.m50) * 0.50) + (Number(valores.m25) * 0.25) + (Number(valores.m10) * 0.10) + (Number(valores.m05) * 0.05) + (Number(valores.m01) * 0.01)
  ).toFixed(2));
  
  // 💡 LÓGICA BLINDADA: Filtra signos negativos y letras especiales inmediatamente
  const handleInputChange = (key: keyof Denominaciones, value: string) => { 
    const cleanVal = value.replace(/[-eE+]/g, '');
    setValores(prev => ({ ...prev, [key]: cleanVal === '' ? '' : Math.abs(Number(cleanVal)) })); 
  };
  
  const handleDatoDigital = (platId: string, tipo: string, campo: 'saldo' | 'ventas' | 'premios', value: string) => { 
    const cleanVal = value.replace(/[-eE+]/g, '');
    setDatosDigitales(prev => ({ ...prev, [platId]: { ...prev[platId], tipo, [campo]: cleanVal === '' ? '' : Math.abs(Number(cleanVal)) } })); 
  };

  const totalIngresosTransf = listaTransferencias.filter(t => ['SALE_RECHARGE', 'PROVIDER_FUNDING', 'BANK_TO_CASH_TRANSFER'].includes(t.transaction_type)).reduce((acc, curr) => acc + Number(curr.amount), 0);
  const totalEgresosTransf = listaTransferencias.filter(t => t.transaction_type === 'PRIZE_PAYMENT').reduce((acc, curr) => acc + Number(curr.amount), 0);
  const totalGastosOp = listaGastos.filter(t => t.transaction_type === 'OPERATING_EXPENSE').reduce((acc, curr) => acc + Number(curr.amount), 0);
  const totalRetirosYRemesas = 
    listaTransferencias.filter(t => ['CASH_TO_BANK_TRANSFER', 'CASH_TO_VAULT_TRANSFER', 'PROVIDER_PAYMENT'].includes(t.transaction_type)).reduce((acc, curr) => acc + Number(curr.amount), 0);

  const billetesRestringidos = [{ key: 'b100', label: '$100', val: 100 }, { key: 'b50', label: '$50', val: 50 }];
  const billetesRegulares = [{ key: 'b20', label: '$20', val: 20 }, { key: 'b10', label: '$10', val: 10 }, { key: 'b5', label: '$5', val: 5 }, { key: 'b1', label: '$1', val: 1 }];
  const monedasRegulares = [{ key: 'm50', label: '50¢', val: 0.50 }, { key: 'm25', label: '25¢', val: 0.25 }, { key: 'm10', label: '10¢', val: 0.10 }, { key: 'm05', label: '5¢', val: 0.05 }];
  const monedasRestringidas = [{ key: 'm01', label: '1¢', val: 0.01 }];

  const API_URL_STATIC = "/api";
  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL_STATIC}${config.logo_url}`) : null;
  const companyName = config?.company_name || "Cash Money";

  if (!isClient) return (
    <div className="h-screen w-full bg-slate-50 flex items-center justify-center font-sans">
      <div className="text-center text-slate-400 font-bold animate-pulse text-sm">Cargando Terminal...</div>
    </div>
  );

  return (
    <div className="h-screen w-full bg-slate-50 flex flex-col overflow-hidden font-sans text-slate-800">
      
      <header className="bg-white border-b border-slate-200 flex flex-wrap items-center justify-between px-4 lg:px-6 shrink-0 z-30 shadow-sm py-2 gap-y-2 min-h-[4rem]">
        
        <div className="flex items-center gap-3 shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              {companyName.charAt(0)}
            </div>
          )}
          <span className="font-bold text-lg tracking-tight hidden sm:block truncate">{companyName}</span>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-sm sm:text-base font-black text-slate-800 tracking-tight">
              Punto de Venta (POS)
            </h1>
            {/* 💡 BOTÓN SINCRONIZAR REUBICADO */}
            <button 
              onClick={handleSincronizarPermisos} 
              disabled={isRefreshingPerms}
              title="Sincronizar permisos con el servidor"
              className="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 flex items-center justify-center transition-all shadow-sm cursor-pointer disabled:opacity-50"
            >
              {isRefreshingPerms ? <span className="animate-spin text-sm">⏳</span> : <span className="text-sm">🔄</span>}
            </button>
          </div>
          {isGlobalAdmin && adminAgenciesList.length > 0 && (
            <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 shadow-sm">
              <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 mr-2">Simular en:</span>
              <select 
                value={currentAgencyId || ''} 
                onChange={(e) => {
                  const ag = adminAgenciesList.find(a => a.id === e.target.value);
                  if (ag) {
                    setCurrentAgencyId(ag.id);
                    setCodigoAgencia(ag.code);
                    setNombreAgencia(ag.name);
                  }
                }}
                className="bg-transparent text-xs font-bold text-blue-800 outline-none cursor-pointer"
              >
                {adminAgenciesList.map(ag => (
                  <option key={ag.id} value={ag.id}>{ag.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 overflow-x-auto scrollbar-hide order-last md:order-none w-full md:w-auto pt-1 md:pt-0">
          
          {estadoCaja === 'OPEN' && fechaApertura ? (
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-lg shrink-0 whitespace-nowrap">
              <span>⏱️</span> <span className="text-blue-600 font-mono tracking-tight">{new Date(fechaApertura).toLocaleTimeString('es-EC', { hour: '2-digit', minute:'2-digit' })}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-2.5 py-1.5 rounded-lg shrink-0 whitespace-nowrap">
              Sin aperturar
            </div>
          )}

          {enableDebugReopen && estadoCaja === 'CLOSED' && (
             <button onClick={handleReabrirCierre} className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg shadow-sm hover:bg-indigo-100 transition-colors cursor-pointer text-indigo-700 text-[10px] font-bold uppercase tracking-widest shrink-0 whitespace-nowrap">
               <span>⏪</span> <span className="hidden xl:block">Revertir Cierre</span>
             </button>
          )}

          {enableTimeMachine && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 p-1.5 rounded-lg shadow-sm shrink-0 whitespace-nowrap">
              <span className="text-sm animate-pulse">⏳</span>
              <input type="datetime-local" value={debugDate} onChange={(e) => setDebugDate(e.target.value)} className="bg-transparent text-[10px] font-mono font-bold text-amber-900 outline-none cursor-pointer w-[125px]"/>
              {debugDate && <button onClick={() => setDebugDate('')} className="w-4 h-4 bg-amber-200 hover:bg-amber-300 text-amber-800 rounded-md flex items-center justify-center text-[10px] font-bold transition-colors">✕</button>}
            </div>
          )}

          <div className="shrink-0">
            {estadoCaja === 'LOADING' ? (
               <span className="px-3 py-1.5 text-slate-400 font-bold animate-pulse text-[10px] whitespace-nowrap">Verificando...</span>
            ) : estadoCaja === 'OPEN' ? (
               <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg font-black text-[10px] flex items-center gap-2 shadow-sm tracking-wide whitespace-nowrap">
                 <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div> ABIERTA
               </span>
            ) : (
               <span className="bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-lg font-black text-[10px] flex items-center gap-2 shadow-sm tracking-wide whitespace-nowrap">
                 <div className="w-2 h-2 rounded-full bg-rose-500"></div> CERRADA
               </span>
            )}
          </div>

        </div>

        <div className="relative shrink-0">
          <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 hover:bg-slate-50 p-1 rounded-2xl border border-transparent hover:border-slate-200 transition-colors cursor-pointer focus:outline-none">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-bold text-slate-800 leading-none">{userName}</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">{userRole}</p>
            </div>
            {userAvatar ? (
               <img src={userAvatar} alt={userName} className="w-9 h-9 rounded-full border border-slate-200 shadow-sm object-cover" />
            ) : (
               <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-200 text-blue-600 font-bold flex items-center justify-center text-sm shadow-sm">
                 {userInitials}
               </div>
            )}
          </button>
          
          {isProfileMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-100 rounded-2xl shadow-xl py-2 z-50 animate-fade-in">
              <div className="px-4 py-3 border-b border-slate-100 mb-2 sm:hidden">
                <p className="text-sm font-bold text-slate-800">{userName}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{userRole}</p>
              </div>
              <button onClick={handleLogout} className="w-full text-left flex items-center gap-3 px-5 py-2.5 text-sm text-rose-600 hover:bg-rose-50 font-bold transition-colors cursor-pointer">
                🚪 Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        
        <aside className="w-[90px] lg:w-[110px] bg-white border-r border-slate-200 flex flex-col items-center gap-2.5 p-2 lg:p-3 overflow-y-auto shrink-0 z-20 shadow-[1px_0_10px_rgba(0,0,0,0.02)] scrollbar-hide">
          
          {['APERTURA', 'ARQUEO', 'TRANSFERENCIA', 'GASTOS', 'RETIROS', 'CREDITOS', 'CIERRE'].map((accion) => {
            const isDisabled = (estadoCaja === 'CLOSED' && accion !== 'APERTURA') || (estadoCaja === 'OPEN' && accion === 'APERTURA') || estadoCaja === 'LOADING';
            const isActive = accionActiva === accion;
            
            return (
              <button 
                key={accion} 
                onClick={() => !isDisabled && setAccionActiva(accion as AccionCaja)} 
                disabled={isDisabled} 
                className={`flex flex-col items-center justify-center gap-1.5 p-2 w-full rounded-2xl border-2 transition-all group relative ${
                  isDisabled 
                    ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-100 grayscale' 
                    : isActive 
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm scale-105' 
                    : 'border-transparent hover:border-blue-200 bg-white hover:bg-slate-50 shadow-sm hover:shadow'
                }`}
              >
                <div className={`text-3xl lg:text-4xl shrink-0 transition-transform ${isActive ? 'scale-110 drop-shadow-sm' : 'group-hover:scale-110'}`}>
                  {accion === 'APERTURA' ? '📥' : accion === 'ARQUEO' ? '⏱️' : accion === 'TRANSFERENCIA' ? '🏦' : accion === 'GASTOS' ? '💸' : accion === 'RETIROS' ? '🏧' : accion === 'CREDITOS' ? '📓' : '📤'}
                </div>
                <span className={`font-bold capitalize text-[9px] lg:text-[10px] tracking-wide text-center w-full truncate ${isActive ? 'text-blue-700' : 'text-slate-600'}`}>
                  {accion === 'RETIROS' ? 'Retiros' : accion === 'CREDITOS' ? 'Créditos' : accion.toLowerCase()}
                </span>
                
                {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-500 rounded-r-full -ml-[2px]" />}
              </button>
            )
          })}
        </aside>

        <main className="flex-1 overflow-y-auto bg-slate-50/50 p-4 lg:p-6 relative z-10 scrollbar-thin">
          <div className="max-w-[1400px] mx-auto space-y-6 pb-24">

            {accionActiva ? (
              <div className="animate-fade-in-up mt-2">
                
                {accionActiva === 'CREDITOS' && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-1 bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-fit">
                      <h2 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><span>📓</span> Créditos y Abonos</h2>
                      <div className="space-y-5">
                        
                        <div className="flex bg-slate-100 p-1.5 rounded-xl">
                          <button onClick={() => setCreditoData({...creditoData, tipo: 'OTORGAR'})} className={`flex-1 py-2.5 rounded-lg font-bold text-xs transition-all ${creditoData.tipo === 'OTORGAR' ? 'bg-white shadow-sm text-rose-600 border border-rose-100' : 'text-slate-500 hover:text-slate-700'}`}>🔴 Aumentar Deuda</button>
                          <button onClick={() => setCreditoData({...creditoData, tipo: 'ABONO'})} className={`flex-1 py-2.5 rounded-lg font-bold text-xs transition-all ${creditoData.tipo === 'ABONO' ? 'bg-white shadow-sm text-emerald-600 border border-emerald-100' : 'text-slate-500 hover:text-slate-700'}`}>🟢 Cobrar Deuda</button>
                        </div>

                        {creditoData.tipo === 'OTORGAR' && (
                          <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-xl flex gap-3 items-start animate-fade-in">
                             <span className="text-lg">⚠️</span>
                             <p className="text-[10px] text-rose-800 font-medium leading-tight">
                               <b>Atención:</b> Está registrando una deuda. Al confirmar, <b>se enviará una alerta roja a gerencia</b> indicando el valor fiado.
                             </p>
                          </div>
                        )}

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Seleccione al Cliente</label>
                          <select 
                            value={creditoData.clientId} 
                            onChange={(e) => {
                              if (e.target.value === 'NEW') {
                                setIsNewClient(true);
                                setCreditoData({...creditoData, clientId: 'NEW'});
                              } else {
                                setIsNewClient(false);
                                setCreditoData({...creditoData, clientId: e.target.value});
                              }
                            }} 
                            className="w-full text-sm font-bold p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 cursor-pointer text-slate-700 transition-shadow"
                          >
                            <option value="" disabled>-- Seleccione cliente --</option>
                            <option value="NEW" className="font-black text-blue-600">➕ CREAR NUEVO CLIENTE</option>
                            {clientesAgencia.map(c => <option key={c.id} value={c.id}>{c.nombres} (Deuda: ${Number(c.total_debt || 0).toFixed(2)})</option>)}
                          </select>
                        </div>

                        {isNewClient && (
                          <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl space-y-3 mt-3 animate-fade-in">
                            <p className="text-[10px] font-black text-blue-800 uppercase tracking-widest flex items-center gap-1"><span>👤</span> Datos del Nuevo Cliente</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase">Cédula</label>
                                <input type="text" value={newClientData.cedula} onChange={e=>setNewClientData({...newClientData, cedula: e.target.value})} className="w-full p-2 text-xs border border-blue-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500" placeholder="131..."/>
                              </div>
                              <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase">Celular (WhatsApp)</label>
                                <input type="text" value={newClientData.telefono} onChange={e=>setNewClientData({...newClientData, telefono: e.target.value})} className="w-full p-2 text-xs border border-blue-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500" placeholder="099..."/>
                              </div>
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-slate-500 uppercase">Nombres Completos</label>
                              <input type="text" value={newClientData.nombres} onChange={e=>setNewClientData({...newClientData, nombres: e.target.value})} className="w-full p-2 text-xs border border-blue-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500" placeholder="Ej. Juan Pérez"/>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Monto Operación</label>
                          <div className="relative">
                            <span className={`absolute left-4 top-3 font-black text-xl ${creditoData.tipo === 'ABONO' ? 'text-emerald-500' : 'text-rose-500'}`}>$</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0.00" value={creditoData.monto} onChange={(e) => setCreditoData({...creditoData, monto: e.target.value.replace(/[-eE+]/g, '')})} className="w-full text-2xl font-black p-2.5 pl-10 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-800 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Descripción</label>
                          <input type="text" placeholder={creditoData.tipo === 'ABONO' ? 'Ej: Abono de $10' : 'Ej: Recarga Listobet sin pagar'} value={creditoData.descripcion} onChange={(e) => setCreditoData({...creditoData, descripcion: e.target.value})} className="w-full text-sm font-medium p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-700 transition-shadow"/>
                        </div>
                        
                        <button onClick={handleAbrirConfirmacionCredito} disabled={isLoading || !creditoData.monto || (!isNewClient && !creditoData.clientId)} className={`w-full py-4 text-white font-black rounded-xl shadow-lg uppercase tracking-widest mt-4 text-sm transition-all active:scale-95 disabled:opacity-50 ${creditoData.tipo === 'ABONO' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-500 hover:bg-rose-600'}`}>
                          {creditoData.tipo === 'ABONO' ? 'REGISTRAR PAGO' : 'AUMENTAR DEUDA'}
                        </button>
                      </div>
                    </div>
                    <div className="xl:col-span-2">
                      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-[600px] flex flex-col">
                         <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 pb-3 flex justify-between shrink-0"><span>Historial de Abonos y Créditos</span><span className="text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Hoy</span></h3>
                         <div className="flex-1 overflow-y-auto scrollbar-thin pr-2">
                           {listaTransferencias.filter(t => ['CLIENT_DEBT_PAYMENT', 'CREDIT_GIVEN'].includes(t.transaction_type) || (t.transaction_type === 'SALE_RECHARGE' && t.payment_method === 'CREDIT')).length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-full text-slate-400"><span className="text-5xl mb-3">📓</span><p className="text-sm font-bold">Cero créditos registrados hoy</p></div>
                           ) : (
                              <div className="space-y-3">
                                {listaTransferencias.filter(t => ['CLIENT_DEBT_PAYMENT', 'CREDIT_GIVEN'].includes(t.transaction_type) || (t.transaction_type === 'SALE_RECHARGE' && t.payment_method === 'CREDIT')).map((tx) => {
                                  const isAbono = tx.transaction_type === 'CLIENT_DEBT_PAYMENT';
                                  return (
                                  <div key={tx.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 transition-colors">
                                    <div className="flex items-center gap-4">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-inner text-lg ${isAbono ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>{isAbono ? '💰' : '📝'}</div>
                                      <div>
                                        <p className="text-sm font-bold text-slate-800">{tx.description}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase">{formatearFecha(tx.created_at)}</p>
                                          {tx.creador && (
                                            <span className="text-[8px] font-black bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-widest border border-slate-300">
                                              👤 {tx.creador}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <span className={`text-lg font-mono font-black ${isAbono ? 'text-emerald-600' : 'text-rose-600'}`}>{isAbono ? '+' : '-'}${Math.abs(Number(tx.amount)).toFixed(2)}</span>
                                    </div>
                                  </div>
                                  )
                                })}
                              </div>
                           )}
                         </div>
                      </div>
                    </div>
                  </div>
                )}

                {accionActiva === 'RETIROS' && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-1 bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-fit">
                      <h2 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><span>🏧</span> Retiros y Entregas</h2>
                      <div className="space-y-5">
                        <div className="flex flex-col gap-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Destino del Dinero</label>
                          <div className="flex bg-slate-100 p-1.5 rounded-xl">
                            <button onClick={() => setRetiroData({...retiroData, destino: 'BANCO'})} className={`flex-1 py-2.5 rounded-lg font-bold text-[11px] transition-colors ${retiroData.destino === 'BANCO' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}>🏦 A Banco</button>
                            <button onClick={() => setRetiroData({...retiroData, destino: 'BOVEDA'})} className={`flex-1 py-2.5 rounded-lg font-bold text-[11px] transition-colors ${retiroData.destino === 'BOVEDA' ? 'bg-white shadow-sm text-amber-700' : 'text-slate-500 hover:text-slate-700'}`}>🛡️ A Supervisor</button>
                            <button onClick={() => setRetiroData({...retiroData, destino: 'PROVEEDOR'})} className={`flex-1 py-2.5 rounded-lg font-bold text-[11px] transition-colors ${retiroData.destino === 'PROVEEDOR' ? 'bg-white shadow-sm text-purple-700' : 'text-slate-500 hover:text-slate-700'}`}>🚚 A Proveedor</button>
                          </div>
                        </div>

                        {retiroData.destino === 'PROVEEDOR' && (
                          <div className="animate-fade-in space-y-4 mt-2 p-4 bg-purple-50 rounded-2xl border border-purple-100">
                            <div>
                              <label className="text-[10px] font-bold text-purple-800 uppercase tracking-wider mb-2 block">Origen de los fondos:</label>
                              <div className="flex gap-2 bg-white p-1 rounded-xl">
                                <button onClick={() => setRetiroData({...retiroData, metodoPago: 'CASH'})} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${retiroData.metodoPago === 'CASH' ? 'bg-amber-50 text-amber-700 border border-amber-200 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}>💵 Efectivo (Caja)</button>
                                <button onClick={() => setRetiroData({...retiroData, metodoPago: 'BANK_TRANSFER'})} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${retiroData.metodoPago === 'BANK_TRANSFER' ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}>🏦 Transferencia</button>
                              </div>
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-purple-800 uppercase tracking-wider mb-2 block">Seleccione el Proveedor</label>
                              <select value={retiroData.plataformaId} onChange={(e) => setRetiroData({...retiroData, plataformaId: e.target.value})} className="w-full text-sm font-bold p-3 border border-purple-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 bg-white cursor-pointer text-slate-800">
                                <option value="" disabled>-- Elija proveedor --</option>
                                {plataformasActivas.map(plat => <option key={plat.id} value={plat.id}>{plat.name}</option>)}
                              </select>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Monto a retirar</label>
                          <div className="relative">
                            <span className="absolute left-4 top-3 font-black text-xl text-slate-400">$</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0.00" value={retiroData.monto} onChange={(e) => setRetiroData({...retiroData, monto: e.target.value.replace(/[-eE+]/g, '')})} className="w-full text-2xl font-black p-2.5 pl-10 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-800 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Referencia / Observación</label>
                          <input type="text" placeholder={retiroData.destino === 'BANCO' ? 'Ej: Papeleta #12345' : retiroData.destino === 'PROVEEDOR' ? 'Ej: Entregado a Recaudador' : 'Ej: Entregado a Supervisor Juan'} value={retiroData.descripcion} onChange={(e) => setRetiroData({...retiroData, descripcion: e.target.value})} className="w-full text-sm font-medium p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-700 transition-shadow"/>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Comprobante (Opcional)</label>
                          <label htmlFor="retiro-file" className={`cursor-pointer flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-4 text-center transition-all hover:bg-slate-50 ${retiroFile ? 'border-blue-500 bg-blue-50/50' : 'border-slate-300 bg-slate-50'}`}>
                            <input type="file" className="hidden" id="retiro-file" accept="image/*" onChange={(e) => { if(e.target.files && e.target.files[0]) setRetiroFile(e.target.files[0]) }}/>
                            {retiroFile ? (
                              <div className="flex flex-col items-center gap-2"><span className="text-3xl">✅</span><p className="text-xs font-bold text-slate-700 truncate max-w-[200px]">{retiroFile.name}</p></div>
                            ) : ( <div className="flex items-center gap-3"><span className="text-2xl">📸</span><p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Adjuntar foto</p></div> )}
                          </label>
                        </div>

                        <button onClick={handleAbrirConfirmacionRetiro} disabled={isLoading || !retiroData.monto} className="w-full py-4 text-white font-black rounded-xl shadow-lg uppercase tracking-widest mt-4 bg-slate-900 hover:bg-black text-sm transition-transform active:scale-95 disabled:opacity-50">
                          PROCESAR ENTREGA
                        </button>
                      </div>
                    </div>
                    
                    <div className="xl:col-span-2">
                      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-[600px] flex flex-col">
                         <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 pb-3 flex justify-between shrink-0">
                           <span>Historial de Retiros de Hoy</span>
                           <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">Total: ${listaTransferencias.filter(t => ['CASH_TO_BANK_TRANSFER', 'CASH_TO_VAULT_TRANSFER', 'PROVIDER_PAYMENT'].includes(t.transaction_type)).reduce((a, b) => a + Number(b.amount), 0).toFixed(2)}</span>
                         </h3>
                         <div className="flex-1 overflow-y-auto scrollbar-thin pr-2">
                           {listaTransferencias.filter(t => ['CASH_TO_BANK_TRANSFER', 'CASH_TO_VAULT_TRANSFER', 'PROVIDER_PAYMENT'].includes(t.transaction_type)).length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-full text-slate-400"><span className="text-5xl mb-3">🏧</span><p className="text-sm font-bold">Cero retiros registrados hoy</p></div>
                           ) : (
                              <div className="space-y-3">
                                {listaTransferencias.filter(t => ['CASH_TO_BANK_TRANSFER', 'CASH_TO_VAULT_TRANSFER', 'PROVIDER_PAYMENT'].includes(t.transaction_type)).map((retiro) => {
                                  const isProv = retiro.transaction_type === 'PROVIDER_PAYMENT';
                                  const isBank = retiro.transaction_type === 'CASH_TO_BANK_TRANSFER';
                                  return (
                                  <div key={retiro.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-300 transition-colors">
                                    <div className="flex items-center gap-4">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-inner text-lg ${isProv ? 'bg-purple-100 text-purple-600' : isBank ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>{isProv ? '🚚' : isBank ? '🏦' : '🛡️'}</div>
                                      <div>
                                        <p className="text-sm font-bold text-slate-800">{retiro.description}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <p className="text-[10px] text-slate-500 font-bold tracking-wider uppercase">
                                            {formatearFecha(retiro.created_at)} • <span className={retiro.payment_method === 'CASH' ? 'text-amber-600' : 'text-blue-600'}>{retiro.payment_method === 'CASH' ? 'Efectivo' : 'Banco'}</span>
                                          </p>
                                          {retiro.creador && (
                                            <span className="text-[8px] font-black bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-widest border border-slate-300">
                                              👤 {retiro.creador}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg font-mono font-black text-slate-800 mr-2">-${Math.abs(Number(retiro.amount)).toFixed(2)}</span>
                                      {retiro.evidence_url ? (
                                        <a href={retiro.evidence_url.startsWith('/api') ? retiro.evidence_url : `/api${retiro.evidence_url}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-sm hover:bg-slate-50">👁️</a>
                                      ) : (
                                        <button onClick={() => setModalUpload({ isOpen: true, type: 'TRANSFERENCIA', targetId: retiro.id, title: 'Comprobante Retiro' })} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-amber-200 text-amber-600 shadow-sm text-sm hover:bg-amber-50 cursor-pointer">📸</button>
                                      )}
                                      <button onClick={() => eliminarTransaccion(retiro.id, 'TRANSFERENCIA')} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 shadow-sm text-sm transition-colors cursor-pointer" title="Eliminar Transacción">🗑️</button>
                                    </div>
                                  </div>
                                  )
                                })}
                              </div>
                           )}
                         </div>
                      </div>
                    </div>
                  </div>
                )}

                {accionActiva === 'GASTOS' && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-1 bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-fit">
                      <h2 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><span>💸</span> Gastos Operativos</h2>
                      <div className="space-y-5">
                        <div>
                           <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Se pagó desde:</label>
                           <div className="flex bg-slate-100 p-1.5 rounded-xl">
                             <button onClick={() => setGastoData({...gastoData, metodoPago: 'CASH'})} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-colors ${gastoData.metodoPago === 'CASH' ? 'bg-white text-amber-700 shadow-sm border border-amber-200/50' : 'text-slate-500 hover:text-slate-700'}`}>💵 Caja Efectivo</button>
                             <button onClick={() => setGastoData({...gastoData, metodoPago: 'BANK_TRANSFER'})} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-colors ${gastoData.metodoPago === 'BANK_TRANSFER' ? 'bg-white text-blue-700 shadow-sm border border-blue-200/50' : 'text-slate-500 hover:text-slate-700'}`}>🏦 Banco Agencia</button>
                           </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Clasificación del Gasto</label>
                          <select value={gastoData.categoria} onChange={(e) => setGastoData({...gastoData, categoria: e.target.value})} className="w-full text-sm font-bold p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-rose-500 bg-slate-50 cursor-pointer text-slate-700 transition-shadow">
                            {CATEGORIAS_GASTO.map(cat => ( <option key={cat.id} value={cat.id}>{cat.label}</option> ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Monto Pagado</label>
                          <div className="relative">
                            <span className="absolute left-4 top-3 font-black text-xl text-rose-500">$</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0.00" value={gastoData.monto} onChange={(e) => setGastoData({...gastoData, monto: e.target.value.replace(/[-eE+]/g, '')})} className="w-full text-2xl font-black p-2.5 pl-10 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-rose-500 bg-rose-50/30 text-slate-800 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Motivo / Descripción</label>
                          <input type="text" placeholder="Ej: Compra de Escoba" value={gastoData.descripcion} onChange={(e) => setGastoData({...gastoData, descripcion: e.target.value})} className="w-full text-sm font-medium p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-rose-500 bg-slate-50 text-slate-700 transition-shadow"/>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Factura o Recibo (Opcional)</label>
                          <label htmlFor="gasto-file" className={`cursor-pointer flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-4 text-center transition-colors hover:bg-slate-50 ${gastoFile ? 'border-rose-400 bg-rose-50/50' : 'border-slate-300 bg-slate-50'}`}>
                            <input type="file" className="hidden" id="gasto-file" accept="image/*" onChange={(e) => { if(e.target.files && e.target.files[0]) setGastoFile(e.target.files[0]) }}/>
                            {gastoFile ? ( 
                              <div className="flex flex-col items-center gap-2"><span className="text-3xl">✅</span><p className="text-xs font-bold truncate max-w-[200px]">{gastoFile.name}</p></div>
                            ) : ( 
                              <div className="flex items-center gap-3"><span className="text-2xl">📸</span><p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Adjuntar foto</p></div> 
                            )}
                          </label>
                        </div>
                        <button onClick={registrarGasto} disabled={isLoading || !gastoData.monto} className="w-full py-4 text-white font-black rounded-xl shadow-lg uppercase tracking-widest mt-4 bg-slate-900 hover:bg-black text-sm transition-transform active:scale-95 disabled:opacity-50">
                          {isLoading ? 'GUARDANDO...' : 'REGISTRAR SALIDA'}
                        </button>
                      </div>
                    </div>
                    
                    <div className="xl:col-span-2">
                      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-[600px] flex flex-col">
                        <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 pb-3 flex justify-between shrink-0">
                          <span>Historial de Egresos Operativos</span>
                          <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">Total: ${listaGastos.reduce((a, b) => a + Number(b.amount), 0).toFixed(2)}</span>
                        </h3>
                        <div className="flex-1 overflow-y-auto scrollbar-thin pr-2">
                          {listaGastos.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400"><span className="text-5xl mb-3">💸</span><p className="text-sm font-bold">Cero gastos registrados hoy</p></div>
                          ) : (
                            <div className="space-y-3">
                              {listaGastos.map((gasto) => (
                                <div key={gasto.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-rose-300 transition-colors">
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-inner bg-rose-100 text-rose-600 text-lg">🛒</div>
                                    <div>
                                      <p className="text-sm font-bold text-slate-800">{gasto.description}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <p className="text-[10px] font-bold text-slate-500 tracking-wider uppercase">
                                          {formatearFecha(gasto.created_at)} • <span className={gasto.payment_method === 'CASH' ? 'text-amber-600' : 'text-blue-600'}>{gasto.payment_method === 'CASH' ? 'Efectivo' : 'Banco'}</span>
                                        </p>
                                        {gasto.creador && (
                                          <span className="text-[8px] font-black bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-widest border border-slate-300">
                                            👤 {gasto.creador}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg font-mono font-black text-rose-600 mr-2">-${Math.abs(Number(gasto.amount)).toFixed(2)}</span>
                                    {gasto.evidence_url ? (
                                      <a href={gasto.evidence_url.startsWith('/api') ? gasto.evidence_url : `/api${gasto.evidence_url}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-sm hover:bg-slate-50">👁️</a>
                                    ) : (
                                      <button onClick={() => setModalUpload({ isOpen: true, type: 'GASTO', targetId: gasto.id, title: 'Recibo Gasto' })} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-amber-200 text-amber-600 shadow-sm text-sm hover:bg-amber-50 cursor-pointer">📸</button>
                                    )}
                                    <button onClick={() => eliminarTransaccion(gasto.id, 'GASTO')} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 shadow-sm text-sm transition-colors cursor-pointer" title="Eliminar Transacción">🗑️</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {accionActiva === 'TRANSFERENCIA' && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                     <div className="xl:col-span-1 bg-white rounded-3xl shadow-sm border border-slate-200 p-6 lg:p-8 h-fit">
                      <h2 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><span>🏦</span> Banco a Caja</h2>
                      <div className="space-y-5">
                        
                        <div className="flex bg-slate-100 p-1.5 rounded-xl">
                          <button onClick={() => setTransData({...transData, tipo: 'INGRESO'})} className={`flex-1 py-2.5 rounded-lg font-bold text-xs transition-all ${transData.tipo === 'INGRESO' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-500 hover:text-slate-700'}`}>⬇️ Dinero Recibido</button>
                          <button onClick={() => setTransData({...transData, tipo: 'EGRESO'})} className={`flex-1 py-2.5 rounded-lg font-bold text-xs transition-all ${transData.tipo === 'EGRESO' ? 'bg-white shadow-sm text-rose-600' : 'text-slate-500 hover:text-slate-700'}`}>⬆️ Pago Entregado</button>
                        </div>

                        {transData.tipo === 'INGRESO' && (
                           <div className="grid grid-cols-1 gap-3 mt-2 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                              <label className="flex items-center gap-3 text-xs font-bold text-slate-700 cursor-pointer">
                                <input type="radio" checked={transData.subTipo === 'SALE_RECHARGE'} onChange={() => setTransData({...transData, subTipo: 'SALE_RECHARGE'})} className="w-4 h-4 text-emerald-600 border-slate-300" /> 
                                Venta Regular (Cliente deposita)
                              </label>
                              <label className="flex items-center gap-3 text-xs font-bold text-slate-700 cursor-pointer">
                                <input type="radio" checked={transData.subTipo === 'PROVIDER_FUNDING'} onChange={() => setTransData({...transData, subTipo: 'PROVIDER_FUNDING'})} className="w-4 h-4 text-purple-600 border-slate-300" /> 
                                Fondeo B2B (Proveedor deposita)
                              </label>
                              <label className="flex items-center gap-3 text-xs font-bold text-slate-700 cursor-pointer border-t border-slate-200 pt-3 mt-1">
                                <input type="radio" checked={transData.subTipo === 'BANK_TO_CASH_TRANSFER'} onChange={() => setTransData({...transData, subTipo: 'BANK_TO_CASH_TRANSFER'})} className="w-4 h-4 text-blue-600 border-slate-300" /> 
                                Fondeo interno a Caja (Retiro de Banco)
                              </label>
                           </div>
                        )}

                        {transData.tipo === 'INGRESO' && transData.subTipo === 'PROVIDER_FUNDING' && (
                          <div className="animate-fade-in mt-2">
                            <label className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-2 block">Proveedor Originario</label>
                            <select value={transData.plataformaId} onChange={(e) => setTransData({...transData, plataformaId: e.target.value})} className="w-full text-sm font-bold p-3 border border-purple-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 bg-white cursor-pointer text-slate-800 transition-shadow">
                              <option value="" disabled>-- Seleccione proveedor --</option>
                              {plataformasActivas.map(plat => <option key={plat.id} value={plat.id}>{plat.name}</option>)}
                            </select>
                          </div>
                        )}

                        {transData.tipo === 'INGRESO' && transData.subTipo === 'BANK_TO_CASH_TRANSFER' && (
                          <div className="animate-fade-in mt-2 p-4 bg-blue-50/80 border border-blue-200 rounded-2xl text-xs text-blue-900 leading-relaxed shadow-sm">
                            <b>🏦 Uso exclusivo interno:</b> Selecciona esta opción si retiraste dinero de la cuenta bancaria de la empresa y lo vas a ingresar <b>físicamente</b> a la caja del local.
                          </div>
                        )}

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Monto de la Transacción</label>
                          <div className="relative">
                            <span className={`absolute left-4 top-3 font-black text-xl ${transData.tipo === 'INGRESO' ? 'text-emerald-500' : 'text-rose-500'}`}>$</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0.00" value={transData.monto} onChange={(e) => setTransData({...transData, monto: e.target.value.replace(/[-eE+]/g, '')})} className="w-full text-2xl font-black p-2.5 pl-10 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-800 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Referencia Bancaria / Observación</label>
                          <input type="text" placeholder={transData.tipo === 'INGRESO' && transData.subTipo === 'PROVIDER_FUNDING' ? 'Ej: Fondeo para pago premio mayor' : 'Ej: Comprobante depósito #9876'} value={transData.descripcion} onChange={(e) => setTransData({...transData, descripcion: e.target.value})} className="w-full text-sm font-medium p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-700 transition-shadow"/>
                        </div>
                        
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Comprobante de Transferencia (Opcional)</label>
                          <label htmlFor="trans-file" className={`cursor-pointer flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-4 text-center transition-all hover:bg-slate-50 ${transFile ? 'border-blue-500 bg-blue-50/50' : 'border-slate-300 bg-slate-50'}`}>
                            <input type="file" className="hidden" id="trans-file" accept="image/*" onChange={(e) => { if(e.target.files && e.target.files[0]) setTransFile(e.target.files[0]) }}/>
                            {transFile ? (
                              <div className="flex flex-col items-center gap-2"><span className="text-3xl">✅</span><p className="text-xs font-bold text-slate-700 truncate max-w-[200px]">{transFile.name}</p></div>
                            ) : ( <div className="flex items-center gap-3"><span className="text-2xl">📸</span><p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Adjuntar foto</p></div> )}
                          </label>
                        </div>
                        
                        <button onClick={handleAbrirConfirmacionTransf} disabled={isLoading || !transData.monto} className={`w-full py-4 text-white font-black rounded-xl shadow-lg transition-transform active:scale-95 disabled:opacity-50 uppercase tracking-widest mt-4 text-sm ${transData.tipo === 'INGRESO' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}>
                          PROCESAR TRANSFERENCIA
                        </button>
                      </div>
                    </div>
                    
                    <div className="xl:col-span-2 space-y-6 flex flex-col">
                      <div className="grid grid-cols-2 gap-6 shrink-0">
                        <div className="bg-emerald-50/50 border border-emerald-200/50 rounded-3xl p-6 shadow-sm">
                          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-1.5">Ingresos de Hoy</p>
                          <p className="text-3xl font-black text-emerald-600">${listaTransferencias.filter(t => ['SALE_RECHARGE', 'PROVIDER_FUNDING', 'BANK_TO_CASH_TRANSFER'].includes(t.transaction_type)).reduce((acc, curr) => acc + Number(curr.amount), 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-rose-50/50 border border-rose-200/50 rounded-3xl p-6 shadow-sm">
                          <p className="text-[10px] font-bold text-rose-700 uppercase tracking-widest mb-1.5">Premios Pagados</p>
                          <p className="text-3xl font-black text-rose-600">${listaTransferencias.filter(t => t.transaction_type === 'PRIZE_PAYMENT').reduce((acc, curr) => acc + Number(curr.amount), 0).toFixed(2)}</p>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 flex-1 h-[450px] flex flex-col">
                        <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-100 pb-3 shrink-0">Historial de Operaciones Bancarias</h3>
                        <div className="flex-1 overflow-y-auto scrollbar-thin pr-2">
                          {listaTransferencias.filter(t => ['SALE_RECHARGE', 'PRIZE_PAYMENT', 'PROVIDER_FUNDING', 'BANK_TO_CASH_TRANSFER'].includes(t.transaction_type)).length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400"><span className="text-5xl mb-3">🧾</span><p className="text-sm font-bold">Sin operaciones bancarias hoy</p></div>
                          ) : (
                            <div className="space-y-3">
                              {listaTransferencias.filter(t => ['SALE_RECHARGE', 'PRIZE_PAYMENT', 'PROVIDER_FUNDING', 'BANK_TO_CASH_TRANSFER'].includes(t.transaction_type)).map((trans) => {
                                const isIngreso = ['SALE_RECHARGE', 'PROVIDER_FUNDING', 'BANK_TO_CASH_TRANSFER'].includes(trans.transaction_type);
                                const isB2B = trans.transaction_type === 'PROVIDER_FUNDING';
                                const isFondeoCaja = trans.transaction_type === 'BANK_TO_CASH_TRANSFER';
                                
                                return (
                                  <div key={trans.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors">
                                    <div className="flex items-center gap-4">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-inner text-lg ${isB2B ? 'bg-purple-100 text-purple-600' : isFondeoCaja ? 'bg-blue-100 text-blue-600' : isIngreso ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>{isB2B ? '💱' : isFondeoCaja ? '🏦' : isIngreso ? '⬇️' : '⬆️'}</div>
                                      <div>
                                        <p className="text-sm font-bold text-slate-800">{trans.description}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                             {formatearFecha(trans.created_at)} • {trans.payment_method === 'CASH' ? 'Efectivo' : 'Banco'}
                                           </p>
                                           {isB2B && <span className="text-[9px] font-black bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase tracking-widest border border-purple-200">B2B</span>}
                                           {isFondeoCaja && <span className="text-[9px] font-black bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase tracking-widest border border-blue-200">A Caja</span>}
                                           {trans.creador && (
                                             <span className="text-[8px] font-black bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-widest border border-slate-300">
                                               👤 {trans.creador}
                                             </span>
                                           )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className={`text-lg font-mono font-black mr-2 ${isB2B ? 'text-purple-600' : isFondeoCaja ? 'text-blue-600' : isIngreso ? 'text-emerald-600' : 'text-rose-600'}`}>{isIngreso ? '+' : '-'}${Math.abs(Number(trans.amount)).toFixed(2)}</span>
                                      {trans.evidence_url ? ( 
                                        <a href={trans.evidence_url.startsWith('/api') ? trans.evidence_url : `/api${trans.evidence_url}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-sm hover:bg-slate-50">👁️</a> 
                                      ) : ( 
                                        <button onClick={() => setModalUpload({ isOpen: true, type: 'TRANSFERENCIA', targetId: trans.id, title: 'Comprobante' })} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-amber-200 text-amber-600 shadow-sm text-sm hover:bg-amber-50 cursor-pointer">📸</button> 
                                      )}
                                      <button onClick={() => eliminarTransaccion(trans.id, 'TRANSFERENCIA')} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 shadow-sm text-sm transition-colors cursor-pointer" title="Eliminar Transacción">🗑️</button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(accionActiva === 'APERTURA' || accionActiva === 'ARQUEO' || accionActiva === 'CIERRE') && (
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-10 mb-8 mt-2 animate-fade-in-up">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 border-b border-slate-100 pb-6 gap-6">
                      <div>
                        <h2 className="text-2xl font-black text-slate-800 mb-1 tracking-tight">Conteo de Dinero Físico</h2>
                        <p className="text-sm font-medium text-slate-500">Ingrese las cantidades exactas de billetes y monedas que hay en caja.</p>
                      </div>
                      <div className="bg-emerald-50/50 px-6 py-4 rounded-2xl border border-emerald-100 text-right w-full sm:w-auto shadow-sm">
                        <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mb-1">Total Físico Contado</p>
                        <p className="text-4xl font-black text-emerald-600 tracking-tighter">${totalCalculado.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="mb-6 flex justify-end">
                      <button onClick={() => setMostrarRestringidos(!mostrarRestringidos)} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl border border-slate-200 shadow-sm cursor-pointer">
                        {mostrarRestringidos ? '🙈 Ocultar Billetes Restringidos' : '👁️ Mostrar $100, $50 y 1¢'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10">
                      <div className="space-y-3">
                        <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>💵</span> Billetes</h3>
                        
                        {mostrarRestringidos && billetesRestringidos.map((item) => (
                          <div key={item.key} className="flex items-center justify-between bg-rose-50/50 p-3 rounded-2xl border border-rose-100 group">
                            <span className="text-base font-black text-rose-600 w-12 pl-2">{item.label}</span>
                            <span className="text-slate-300 text-sm font-bold">x</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0" value={valores[item.key as keyof Denominaciones]} onChange={(e) => handleInputChange(item.key as keyof Denominaciones, e.target.value)} className="w-24 text-center text-xl font-black bg-white border border-rose-200 rounded-xl py-2 outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                            <span className="text-slate-300 font-bold w-4 text-center">=</span>
                            <span className="w-24 text-right pr-2 text-lg font-black text-rose-800">${(Number(valores[item.key as keyof Denominaciones]) * item.val).toFixed(2)}</span>
                          </div>
                        ))}

                        {billetesRegulares.map((item) => (
                          <div key={item.key} className="flex items-center justify-between bg-slate-50/80 p-3 rounded-2xl border border-slate-200 hover:border-blue-300 transition-colors shadow-sm">
                            <span className="text-base font-black text-slate-700 w-12 pl-2">{item.label}</span>
                            <span className="text-slate-300 text-sm font-bold">x</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0" value={valores[item.key as keyof Denominaciones]} onChange={(e) => handleInputChange(item.key as keyof Denominaciones, e.target.value)} className="w-24 text-center text-xl font-black bg-white border border-slate-200 rounded-xl py-2 outline-none focus:ring-2 focus:ring-blue-500 shadow-inner transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                            <span className="text-slate-300 font-bold w-4 text-center">=</span>
                            <span className="w-24 text-right pr-2 text-lg font-black text-slate-800">${(Number(valores[item.key as keyof Denominaciones]) * item.val).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>🪙</span> Monedas</h3>
                        
                        {monedasRegulares.map((item) => (
                          <div key={item.key} className="flex items-center justify-between bg-slate-50/80 p-3 rounded-2xl border border-slate-200 hover:border-blue-300 transition-colors shadow-sm">
                            <span className="text-base font-black text-slate-700 w-12 pl-2">{item.label}</span>
                            <span className="text-slate-300 text-sm font-bold">x</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0" value={valores[item.key as keyof Denominaciones]} onChange={(e) => handleInputChange(item.key as keyof Denominaciones, e.target.value)} className="w-24 text-center text-xl font-black bg-white border border-slate-200 rounded-xl py-2 outline-none focus:ring-2 focus:ring-blue-500 shadow-inner transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                            <span className="text-slate-300 font-bold w-4 text-center">=</span>
                            <span className="w-24 text-right pr-2 text-lg font-black text-slate-800">${(Number(valores[item.key as keyof Denominaciones]) * item.val).toFixed(2)}</span>
                          </div>
                        ))}

                        {mostrarRestringidos && monedasRestringidas.map((item) => (
                          <div key={item.key} className="flex items-center justify-between bg-rose-50/50 p-3 rounded-2xl border border-rose-100 group mt-6">
                            <span className="text-base font-black text-rose-600 w-12 pl-2">{item.label}</span>
                            <span className="text-slate-300 text-sm font-bold">x</span>
                            {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                            <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} placeholder="0" value={valores[item.key as keyof Denominaciones]} onChange={(e) => handleInputChange(item.key as keyof Denominaciones, e.target.value)} className="w-24 text-center text-xl font-black bg-white border border-rose-200 rounded-xl py-2 outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                            <span className="text-slate-300 font-bold w-4 text-center">=</span>
                            <span className="w-24 text-right pr-2 text-lg font-black text-rose-800">${(Number(valores[item.key as keyof Denominaciones]) * item.val).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-slate-200">
                      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                        <div>
                          <h2 className="text-xl font-black text-slate-800 mb-1 flex items-center gap-2">💻 Declaración de Pantallas</h2>
                          <p className="text-sm font-medium text-slate-500">Opcional: Adjunte la captura de pantalla del corte usando el icono de la cámara.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {plataformasActivas.map((plat) => {
                          const isDeportiva = plat.platform_type === 'SPORTS_BETTING';
                          const stateData = datosDigitales[plat.id] || {};
                          const pendingFile = archivosPlataformas[plat.id];
                          const hasImage = !!pendingFile;
                          const previewUrl = pendingFile ? URL.createObjectURL(pendingFile) : '';

                          return (
                            <div key={plat.id} className="bg-white rounded-3xl p-5 border border-slate-200 relative shadow-sm hover:shadow-lg transition-all group">
                              <div className="absolute right-4 top-4 flex gap-2">
                                {hasImage ? (
                                  <>
                                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-200 text-sm">👁️</a>
                                    <button onClick={() => { const nuevos = {...archivosPlataformas}; delete nuevos[plat.id]; setArchivosPlataformas(nuevos); }} className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-200 text-sm cursor-pointer">✕</button>
                                  </>
                                ) : (
                                  <button onClick={() => setModalUpload({ isOpen: true, type: 'PLATAFORMA', targetId: plat.id, title: plat.name })} className="w-8 h-8 rounded-xl bg-slate-50 text-slate-400 border border-slate-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors cursor-pointer shadow-sm group-hover:scale-105">📸</button>
                                )}
                              </div>
                              <div className="pr-16 mb-5 border-b border-slate-100 pb-2">
                                <label className="font-black text-slate-800 block truncate text-base">{plat.name}</label>
                                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{plat.platform_type.replace('_', ' ')}</span>
                              </div>
                              
                              {isDeportiva ? (
                                <div><p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Saldo en Pantalla</p><div className="relative"><span className="absolute left-4 top-3 font-black text-xl text-slate-400">$</span>
                                {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                                <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} value={stateData.saldo || ''} onChange={(e) => handleDatoDigital(plat.id, plat.platform_type, 'saldo', e.target.value)} className="w-full text-2xl font-black bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 outline-none focus:ring-2 focus:ring-blue-500 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/></div></div>
                              ) : (
                                <div className="space-y-4">
                                  <div><p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Total Ventas</p><div className="relative"><span className="absolute left-3 top-2.5 font-black text-emerald-400">$</span>
                                  {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                                  <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} value={stateData.ventas || ''} onChange={(e) => handleDatoDigital(plat.id, plat.platform_type, 'ventas', e.target.value)} className="w-full text-xl font-black bg-emerald-50/30 border border-emerald-200 rounded-xl py-2 pl-8 pr-3 text-emerald-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/></div></div>
                                  <div><p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Premios Pagados</p><div className="relative"><span className="absolute left-3 top-2.5 font-black text-rose-400">$</span>
                                  {/* 💡 BLOQUEO DE SCROLL EN INPUT NUMBER Y RESTRICCIÓN DE TECLAS NEGATIVAS */}
                                  <input type="number" min="0" onWheel={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }} value={stateData.premios || ''} onChange={(e) => handleDatoDigital(plat.id, plat.platform_type, 'premios', e.target.value)} className="w-full text-xl font-black bg-rose-50/30 border border-rose-200 rounded-xl py-2 pl-8 pr-3 text-rose-700 outline-none focus:ring-2 focus:ring-rose-500 transition-shadow [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/></div></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-10 space-y-6 max-w-2xl mx-auto">
                      {accionActiva === 'APERTURA' && (
                        <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl shadow-sm">
                          <p className="text-[11px] font-black text-amber-800 uppercase tracking-widest mb-2 flex items-center gap-2"><span>⚠️</span> Nota de Protesta (Opcional)</p>
                          <textarea value={notaProtesta} onChange={(e) => setNotaProtesta(e.target.value)} placeholder="Ej. Faltaron $5 en la gaveta al recibir el turno, reportado al supervisor..." className="w-full text-sm font-medium p-4 border border-amber-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-amber-400 transition-shadow" rows={3} />
                        </div>
                      )}
                      <button onClick={() => setShowSummaryModal(true)} disabled={isLoading || totalCalculado === 0} className="w-full py-5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl transition-transform active:scale-95 disabled:opacity-50 text-lg tracking-widest uppercase cursor-pointer">
                        {isLoading ? 'PROCESANDO...' : `CONFIRMAR ${accionActiva}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[50vh] text-center px-4 animate-fade-in mt-10">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-sm border border-slate-200 mb-6 opacity-80">👈</div>
                <h2 className="text-2xl font-black text-slate-800 mb-2 tracking-tight">Caja Central</h2>
                <p className="text-slate-500 font-medium max-w-md">Seleccione una acción operativa desde el panel izquierdo para comenzar a trabajar en el turno actual.</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* 🚀 💡 NUEVO MODAL DE CONFIRMACIÓN DE TRANSFERENCIA */}
      {confirmTransfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl relative border border-slate-200 text-center">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-blue-100 text-4xl shadow-inner">
              🏦
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">¿Confirmar Operación?</h3>
            <p className="text-slate-500 mb-6 font-medium">Revise detenidamente antes de proceder con el registro bancario.</p>
            
            <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-4 mb-8 border border-slate-100 shadow-inner">
              <div className="flex justify-between items-center border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tipo</span>
                <span className={`font-black text-sm px-3 py-1 rounded-lg border ${transData.tipo === 'INGRESO' ? 'text-emerald-700 bg-emerald-100 border-emerald-200' : 'text-rose-700 bg-rose-100 border-rose-200'}`}>
                  {transData.tipo === 'INGRESO' ? '⬇️ INGRESO' : '⬆️ EGRESO'}
                </span>
              </div>
              <div className="flex flex-col gap-1 border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Detalle</span>
                <span className="font-bold text-slate-700 text-sm truncate">{transData.descripcion}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Monto</span>
                <span className="font-black text-slate-900 text-2xl">${Math.abs(Number(transData.monto)).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setConfirmTransfModal(false)} className="flex-1 py-4 rounded-xl font-bold text-slate-600 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors text-sm cursor-pointer uppercase tracking-wider">Cancelar</button>
              <button onClick={ejecutarTransferencia} className="flex-1 py-4 rounded-xl font-black text-white shadow-md transition-transform active:scale-95 text-sm uppercase tracking-widest cursor-pointer bg-blue-600 hover:bg-blue-700">
                SÍ, REGISTRAR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 💡 NUEVO MODAL DE CONFIRMACIÓN DE RETIRO/ENTREGA */}
      {confirmRetiroModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl relative border border-slate-200 text-center">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-amber-100 text-4xl shadow-inner">
              🏧
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">¿Confirmar Entrega?</h3>
            <p className="text-slate-500 mb-6 font-medium">Verifique que el dinero en efectivo fue entregado o depositado correctamente.</p>
            
            <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-4 mb-8 border border-slate-100 shadow-inner">
              <div className="flex justify-between items-center border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Destino</span>
                <span className="font-black text-sm px-3 py-1 rounded-lg border text-amber-700 bg-amber-100 border-amber-200 uppercase truncate max-w-[150px] sm:max-w-[200px]">
                  {retiroData.destino === 'BOVEDA' ? 'SUPERVISOR' : 
                   retiroData.destino === 'PROVEEDOR' ? 
                     (plataformasActivas.find(p => p.id === retiroData.plataformaId)?.name || 'PROVEEDOR') 
                   : retiroData.destino}
                </span>
              </div>
              <div className="flex flex-col gap-1 border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Detalle</span>
                <span className="font-bold text-slate-700 text-sm truncate">{retiroData.descripcion}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Monto</span>
                <span className="font-black text-slate-900 text-2xl">${Math.abs(Number(retiroData.monto)).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setConfirmRetiroModal(false)} className="flex-1 py-4 rounded-xl font-bold text-slate-600 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors text-sm cursor-pointer uppercase tracking-wider">Cancelar</button>
              <button onClick={ejecutarRetiro} className="flex-1 py-4 rounded-xl font-black text-white shadow-md transition-transform active:scale-95 text-sm uppercase tracking-widest cursor-pointer bg-amber-600 hover:bg-amber-700">
                SÍ, PROCEDER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 💡 MODAL DE CONFIRMACIÓN DE CRÉDITO/DEUDA */}
      {confirmCreditoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl relative border border-slate-200 text-center">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-amber-100 text-4xl shadow-inner">
              ⚠️
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">¿Confirmar Operación?</h3>
            <p className="text-slate-500 mb-6 font-medium">Revise detenidamente antes de proceder. Por seguridad, esta acción <b className="text-rose-600">no se puede borrar</b> del historial.</p>
            
            <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-4 mb-8 border border-slate-100 shadow-inner">
              <div className="flex justify-between items-center border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tipo</span>
                <span className={`font-black text-sm px-3 py-1 rounded-lg border ${creditoData.tipo === 'ABONO' ? 'text-emerald-700 bg-emerald-100 border-emerald-200' : 'text-rose-700 bg-rose-100 border-rose-200'}`}>
                  {creditoData.tipo === 'ABONO' ? '🟢 COBRO DE DEUDA' : '🔴 AUMENTAR DEUDA'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-200 pb-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Cliente</span>
                <span className="font-bold text-slate-700 text-sm truncate max-w-[180px]">
                  {isNewClient ? newClientData.nombres : clientesAgencia.find(c => c.id === creditoData.clientId)?.nombres}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Monto</span>
                <span className="font-black text-slate-900 text-2xl">${Math.abs(Number(creditoData.monto)).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setConfirmCreditoModal(false)} className="flex-1 py-4 rounded-xl font-bold text-slate-600 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors text-sm cursor-pointer uppercase tracking-wider">Cancelar</button>
              <button onClick={ejecutarCreditoAbono} className={`flex-1 py-4 rounded-xl font-black text-white shadow-md transition-transform active:scale-95 text-sm uppercase tracking-widest cursor-pointer ${creditoData.tipo === 'ABONO' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}>
                SÍ, PROCEDER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: SUBIDA DE IMÁGENES */}
      {modalUpload.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl relative border border-slate-200">
            <button onClick={() => setModalUpload({ isOpen: false, type: 'PLATAFORMA', targetId: '', title: '' })} className="absolute top-6 right-6 text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-full w-8 h-8 font-bold text-sm flex items-center justify-center transition-colors cursor-pointer">✕</button>
            <h3 className="text-2xl font-black text-gray-900 mb-1">Subir Captura</h3>
            <p className="text-sm font-medium text-gray-500 mb-8">Adjuntando archivo para <span className="font-bold text-blue-600">{modalUpload.title}</span></p>
            <div className="border-2 border-dashed border-blue-200 rounded-2xl p-8 text-center bg-blue-50/50 transition-colors hover:bg-blue-50">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-sm font-bold text-slate-700 mb-2">Pega la imagen aquí (Ctrl + V)</p>
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-widest mb-6">o selecciona desde tu dispositivo</p>
              <input type="file" id="file-upload" className="hidden" accept="image/png, image/jpeg, image/webp" onChange={(e) => { if (e.target.files && e.target.files[0]) handleFileUpload(e.target.files[0]); }} />
              <label htmlFor="file-upload" className="cursor-pointer inline-block bg-white border border-slate-200 px-6 py-3 rounded-xl text-sm font-bold text-slate-700 shadow-sm hover:shadow transition-all">{isUploading ? 'Subiendo...' : 'Seleccionar Archivo'}</label>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: TICKET DE RESUMEN PARA WHATSAPP */}
      {showSummaryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden border border-slate-200 flex flex-col">
            
            <div className={`p-6 text-center text-white ${accionActiva === 'CIERRE' ? 'bg-rose-600' : accionActiva === 'ARQUEO' ? 'bg-amber-500' : 'bg-blue-600'}`}>
              <h3 className="text-xl font-black tracking-widest uppercase mb-1">TICKET DE {accionActiva}</h3>
              <div className="mt-2 mb-2 inline-flex items-center gap-1.5 bg-black/20 px-3 py-1 rounded-lg text-[10px] shadow-inner">
                <span>📍</span>
                <span className="font-bold uppercase tracking-wider">{nombreAgencia || codigoAgencia || 'Agencia Local'}</span>
              </div>
              <p className="text-[11px] opacity-80 mt-1 font-medium tracking-wide">{new Date(getFechaProceso()).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
            
            <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto bg-slate-50 scrollbar-thin">
              
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm text-center">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Físico Contado</p>
                <p className="text-5xl font-black text-emerald-600 tracking-tighter">${totalCalculado.toFixed(2)}</p>
              </div>

              {plataformasActivas.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Declaración Plataformas</h4>
                  {plataformasActivas.map(plat => {
                    const data = datosDigitales[plat.id];
                    if (!data) return null;
                    return (
                      <div key={plat.id} className="flex justify-between items-center py-2 border-b border-dashed border-slate-200 last:border-0">
                        <span className="font-bold text-slate-700 text-sm">{plat.name}</span>
                        <div className="text-right">
                          {data.saldo && <div className="text-sm font-black text-blue-600">S: ${Number(data.saldo).toFixed(2)}</div>}
                          {data.ventas && <div className="text-sm font-black text-emerald-600">V: ${Number(data.ventas).toFixed(2)}</div>}
                          {data.premios && <div className="text-sm font-black text-rose-600">P: ${Number(data.premios).toFixed(2)}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {(accionActiva === 'CIERRE' || accionActiva === 'ARQUEO') && (
                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm space-y-3 mt-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Resumen Operaciones</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-600">Transf. Recibidas</span>
                    <span className="text-sm font-black text-emerald-600">+${totalIngresosTransf.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-600">Premios Pagados</span>
                    <span className="text-sm font-black text-rose-600">-${totalEgresosTransf.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-600">Gastos Operativos</span>
                    <span className="text-sm font-black text-rose-600">-${totalGastosOp.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-600">Retiros Efectivo</span>
                    <span className="text-sm font-black text-amber-600">-${totalRetirosYRemesas.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {notaProtesta && (
                <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200">
                  <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-1.5 flex items-center gap-1"><span>⚠️</span> Nota Adicional</p>
                  <p className="text-sm text-amber-900 font-medium leading-relaxed">"{notaProtesta}"</p>
                </div>
              )}
            </div>
            
            <div className="p-5 bg-white border-t border-slate-100 flex gap-3">
               <button onClick={() => setShowSummaryModal(false)} className="flex-1 py-3.5 rounded-xl font-bold text-slate-600 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors text-xs uppercase tracking-wider cursor-pointer">Volver</button>
               <button onClick={confirmarYEjecutarAccion} className="flex-1 py-3.5 rounded-xl font-black text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all active:scale-95 uppercase tracking-widest text-xs cursor-pointer">✅ ENVIAR TICKET</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default dynamic(() => Promise.resolve(GestionCajaPageContent), { ssr: false });